import type { Moment } from "moment";
import React, { Component } from "react";
import DatePicker from "react-datepicker";
import { FormattedMessage, injectIntl, type IntlShape } from "react-intl";
import { connect, type ConnectedProps } from "react-redux";
import { NavLink } from "react-router-dom";

import { getAllShipments, trackingLink, UPS_TRACK_LOCALE_MAP } from "~/actions/deliveries";
import { DATE_FORMAT, formatDate } from "~/common/datetime";
import { remoteLog } from "~/common/logging";
import { PersonName } from "~/components/common/PersonName";
import { Layout } from "~/components/ui/layout";
import { Portlet, PortletTitle } from "~/components/ui/portlet";
import {
  Table,
  TableCell,
  TableEmpty,
  TableHead,
  TableLoading,
  TableRow,
} from "~/components/ui/table";
import { setDocumentTitle } from "~/hooks/use-document-title";
import type { RootState } from "~/store";

const mapStateToProps = (state: RootState) => {
  return {
    shipments: state.shipments,
  };
};

const mapDispatchToProps = {
  getShipments: getAllShipments,
};

type ShipmentsListProps = PropsFromRedux & { intl: IntlShape; bareLayout?: boolean };

type ShipmentsListState = {
  startDate: Moment | null;
  isLoading: boolean;
};

class ShipmentsPage extends Component<ShipmentsListProps, ShipmentsListState> {
  constructor(props: ShipmentsListProps) {
    super(props);
    this.state = {
      startDate: null,
      isLoading: false,
    };
    this.onChangeDate = this.onChangeDate.bind(this);
  }

  componentDidCatch(e: Error) {
    remoteLog(e, "shipments_page");
  }

  componentDidMount() {
    void this.props.getShipments();
    setDocumentTitle(this.props.intl.formatMessage({ id: "pat.shipments.header" }));
  }

  componentDidUpdate(prevProps: ShipmentsListProps) {
    if (this.props.shipments !== prevProps.shipments) {
      this.setState({ isLoading: false });
    }
  }

  onChangeDate(e: Moment) {
    this.setState({ isLoading: true });
    const from = e && e._d ? formatDate(e._d)._i : null;
    this.setState({ startDate: e && e._d ? formatDate(e._d) : null });
    this.props.getShipments(from);
  }

  render() {
    const { isLoading } = this.state;
    const { shipments, intl } = this.props;
    const upsLocale = UPS_TRACK_LOCALE_MAP[intl.locale];
    const isEmpty = !isLoading && shipments.data.length == 0;

    return (
      <Layout chrome={!this.props.bareLayout}>
        <Portlet as="main" testId="shipments-page">
          <PortletTitle
            iconClassName="icon-users"
            id="patients-table-block-title"
            testId="shipments-page-title"
          >
            <FormattedMessage id="pat.shipments.header" />
          </PortletTitle>

          <p>
            <FormattedMessage id="pat.shipments.description" />
          </p>

          <label htmlFor="start_date">
            <FormattedMessage id="pat.shipments.selected.date" />
          </label>
          <DatePicker
            id="start_date"
            className="form-control tw-mb-2 max-sm:tw-text-base"
            dateFormat={DATE_FORMAT}
            selected={this.state.startDate}
            onSelect={(e) => this.onChangeDate(e)}
            placeholderText={this.props.intl.formatMessage({ id: "date" })}
          />

          <div className="portlet-body">
            <div id="doctor_pacients_table_wrapper" className="dataTables_wrapper no-footer">
              <Table id="doctor_pacients_table">
                <thead>
                  <TableRow>
                    <TableHead style={{ width: 14 }}>#</TableHead>

                    <TableHead style={{ width: 111 }}>
                      <FormattedMessage id="pat_table.name" />
                    </TableHead>

                    <TableHead style={{ width: 150 }}>
                      <FormattedMessage id="pat.shipments.tracking_number" />
                    </TableHead>
                  </TableRow>
                </thead>

                <tbody>
                  {!shipments.hasBeenLoaded || isLoading ? (
                    <TableLoading columns={3} />
                  ) : isEmpty ? (
                    <TableEmpty columns={3}>
                      <FormattedMessage id="pat_table.patients.not_found" />
                    </TableEmpty>
                  ) : (
                    shipments.data.map((shipment, i) => (
                      <TableRow key={i}>
                        <TableCell className="sorting_1">{shipment.patient.patient_id}</TableCell>

                        <TableCell>
                          <NavLink to={`/pages/patient/${shipment.patient.patient_id}`}>
                            <PersonName person={shipment.patient} />
                          </NavLink>
                        </TableCell>

                        <TableCell>
                          <a
                            href={trackingLink(shipment.tracking_number, shipment.type, upsLocale)}
                            rel="noopener noreferrer"
                            target="_blank"
                          >
                            {shipment.tracking_number}
                          </a>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </tbody>
              </Table>
            </div>
          </div>
        </Portlet>
      </Layout>
    );
  }
}

const connector = connect(mapStateToProps, mapDispatchToProps);
type PropsFromRedux = ConnectedProps<typeof connector>;
export default connector(injectIntl(ShipmentsPage));
